import { useEffect, useState } from 'react'
import { Link as RouterLink, useLocation, useNavigate } from 'react-router-dom'
import { useMutation } from 'react-query'
import { useFormik } from 'formik'
import * as Yup from 'yup'

import { Box, IconButton, Link, Typography } from '@mui/material'
import ArrowBackIosIcon from '@mui/icons-material/ArrowBackIos'
import VisibilityIcon from '@mui/icons-material/Visibility'
import VisibilityOffIcon from '@mui/icons-material/VisibilityOff'

import { userRegister } from 'services/api/signup'
import AdxErrorMessage from 'components/custom/AdxErrorMessage'
import AdxInput from 'components/custom/AdxInput'
import OnboardingContainer from 'components/smart/onboarding-container/OnboardingContainer'
import AdxCheckbox from 'components/custom/AdxCheckbox'
import Astronaut from '../assets/astronaut.svg'
import Bugsnag from '@bugsnag/js'
import AdxButton from 'components/custom/AdxButton'

const SignUpRegister = () => {
  const navigate = useNavigate()
  const location = useLocation()

  // display password states
  const [showPassword, setShowPassword] = useState({
    a: false,
    b: false,
  })

  // helpers functions
  const handleBack = () => {
    navigate('/signup')
  }

  // form field states and function
  const handleShowPassword = (type) => {
    setShowPassword({ ...showPassword, [type]: !showPassword[type] })
  }

  const PasswordEye = ({ type }) => {
    return (
      <IconButton onClick={() => handleShowPassword(type)} sx={{ padding: 0 }}>
        {showPassword[type] ? <VisibilityOffIcon /> : <VisibilityIcon />}
      </IconButton>
    )
  }

  // react query mutate hook
  const { isLoading: RegisterLoading, mutate: RegisterQuery } = useMutation(
    (data) => userRegister(data),
    {
      onSuccess: async (data) => {
        navigate('/signup/waiting-email-verification', {
          state: formik.values,
        })
      },
      onError: async (err) => {
        formik.setFieldError(
          Object.keys(err?.data?.errors)[0],
          err?.data?.errors[Object.keys(err?.data?.errors)[0]][0]
        )
        Bugsnag.notify(err)
      },
    }
  )

  // form states
  const validationSchema = Yup.object({
    name: Yup.string()
      .required('Full name is required')
      .min(10, 'Full name must be at least 10 characters'),
    password: Yup.string()
      .required('Password is required')
      .min(8, 'Password must be at least 8 characters'),
    // .matches(
    //   /^(?=.*[A-Za-z])(?=.*\d)[A-Za-z\d]{8,}$/,
    //   'Password must contain character and number'
    // ),
    password_confirmation: Yup.string()
      .required('Confirm password is required')
      .oneOf([Yup.ref('password'), null], 'Passwords must match'),
    agree_status: Yup.boolean()
      .required('You must agree to the terms and conditions before registering')
      .oneOf([true], 'You must agree to the terms and conditions before registering'),
  })

  const formik = useFormik({
    initialValues: {
      name: '',
      email: location?.state?.email,
      password: '',
      password_confirmation: '',
      agree_status: false,
    },
    validationSchema,
    onSubmit: (values) => {
      RegisterQuery(values)
    },
  })

  // redirect if the user not from signup page
  useEffect(() => {
    if (!location?.state?.fromCheckEmail) {
      navigate('/signup')
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  return (
    <OnboardingContainer
      bgColorRight="alternate.main"
      topRightPhoto="10%"
      leftRightPhoto="75%"
      heightRightPhoto="100%"
      transformRightPhoto={`translate(${-50}%, ${0}%)`}
    >
      <Box
        component="img"
        src={Astronaut}
        position="fixed"
        bottom={0}
        right={0}
        width="calc(100vw * 0.55)"
        display={{ xs: 'none', lg: 'block' }}
      />
      <Box
        height={'100%'}
        width={'100%'}
        display="flex"
        justifyContent={{ xs: 'center', lg: 'flex-end' }}
        alignItems={{ xs: 'center', lg: 'center' }}
      >
        <form
          style={{
            display: 'flex',
            flexDirection: 'column',
            justifyContent: 'center',
            width: '100%',
          }}
          onSubmit={formik.handleSubmit}
        >
          <Box
            width={{ xs: '100%', lg: '500px' }}
            height={{ xs: '90vh', lg: 'unset' }}
            mr={{ lg: 7.6875 }}
            p={{ xs: 2, lg: 0 }}
            position={'relative'}
            display="flex"
            flexDirection="column"
            justifyContent="space-between"
            margin="0 auto"
          >
            <Box>
              <Box mb={4}>
                <Typography variant="h1" mb={1}>
                  Your HR tech universe starts here
                </Typography>
                <Typography variant="body1" textAlign="justify" color="light-gray-text.main">
                  Get started your moonshot by streamline your business HR suites tech and manage
                  your team. Create a Super admin account.
                </Typography>
              </Box>
              <Box display="flex" flexDirection="column" gap={1}>
                <AdxInput
                  label="Full Name"
                  name="name"
                  onChange={formik.handleChange}
                  isErrorShown={formik?.errors?.name}
                  errorMessage={<AdxErrorMessage>{formik?.errors?.name}</AdxErrorMessage>}
                />
                <AdxInput
                  label="Password"
                  name="password"
                  type={showPassword.a ? 'text' : 'password'}
                  onChange={formik.handleChange}
                  isErrorShown={formik?.errors?.password}
                  errorMessage={<AdxErrorMessage>{formik?.errors?.password}</AdxErrorMessage>}
                  endAdornment={<PasswordEye type="a" />}
                />
                <AdxInput
                  label="Confirm password"
                  name="password_confirmation"
                  type={showPassword.b ? 'text' : 'password'}
                  onChange={formik.handleChange}
                  isErrorShown={formik?.errors?.password_confirmation}
                  errorMessage={
                    <AdxErrorMessage>{formik?.errors?.password_confirmation}</AdxErrorMessage>
                  }
                  endAdornment={<PasswordEye type="b" />}
                />

                <Box sx={{ mb: 3, mt: 1 }}>
                  <Box sx={{ display: 'flex', alignItems: 'center' }}>
                    <AdxCheckbox
                      name="agree_status"
                      onChange={formik.handleChange}
                      sx={{ padding: 0, mr: 1 }}
                    />
                    <Typography>
                      I agree to adnexio's{' '}
                      <Link
                        href={'/terms-conditions'}
                        sx={{ color: 'alternate.main', textDecoration: 'none' }}
                        underline="hover"
                      >
                        Terms of Use,
                      </Link>{' '}
                      <Link
                        href={process.env.REACT_APP_ADNEXIO_PRO_URL + '/privacy-policy'}
                        target="_blank"
                        sx={{ color: 'alternate.main', textDecoration: 'none' }}
                        underline="hover"
                      >
                        Privacy Policy and DPA
                      </Link>
                    </Typography>
                  </Box>
                  <AdxErrorMessage>{formik?.errors?.agree_status}</AdxErrorMessage>
                </Box>
              </Box>
            </Box>
            <Box mt={2}>
              <Box mb={2} display="flex" gap={1}>
                <AdxButton
                  variant={'outlined'}
                  onClick={handleBack}
                  sx={{ minWidth: '20px', pl: 3, borderWidth: 2 }}
                >
                  <ArrowBackIosIcon />
                </AdxButton>
                <AdxButton
                  type="submit"
                  fullWidth
                  disabled={RegisterLoading}
                  isLoading={RegisterLoading}
                >
                  Create my account
                </AdxButton>
              </Box>
              <Typography variant="body1" textAlign="center">
                Organization already existed? Sign in as employee{' '}
                <Link
                  component={RouterLink}
                  to={process.env.REACT_APP_ADNEXIO_NEXT_URL + '/signin'}
                  style={{ textDecoration: 'none' }}
                  sx={{ color: 'alternate.main' }}
                >
                  here
                </Link>
              </Typography>
            </Box>
          </Box>
        </form>
      </Box>
    </OnboardingContainer>
  )
}

export default SignUpRegister
